import { useQuery } from "@tanstack/react-query";
import { from, unwrap } from "@vixio/data";
import type { StatusMap } from "@vixio/ui";

export type BatchStatus = "uploaded" | "mapped" | "validated" | "previewed" | "applied";
export const BATCH_STATUS: StatusMap = {
  uploaded: { label: "arquivo lido", tone: "neutral" },
  mapped: { label: "mapeado", tone: "info" },
  validated: { label: "validado", tone: "info" },
  previewed: { label: "prévia conferida", tone: "warn" },
  applied: { label: "aplicado", tone: "ok" },
};
export const ROW_STATUS: StatusMap = {
  pending: { label: "pendente", tone: "neutral" },
  valid: { label: "válida", tone: "info" },
  invalid: { label: "com erro", tone: "crit" },
  applied: { label: "gravada", tone: "ok" },
  skipped: { label: "já importada", tone: "neutral" },
  failed: { label: "falhou", tone: "crit" },
};
export const STEPS = ["Arquivo", "Mapeamento", "Validação", "Prévia", "Aplicar"] as const;
export function stepOf(status: BatchStatus): number {
  return { uploaded: 1, mapped: 2, validated: 3, previewed: 4, applied: 5 }[status];
}

export const FIELD_TYPE: Record<string, string> = {
  text: "texto", email: "e-mail", phone: "telefone", cents: "valor em R$", date: "data", int: "número", bool: "sim ou não", enum: "lista", doc: "CPF/CNPJ", answer: "resposta",
};

export interface TargetField {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  aliases?: string[];
  values?: string[];
}

export interface TargetView {
  key: string;
  module: string;
  label: string;
  min_role: string;
  key_fields: string[];
  require_any: string[];
  prefixes: Record<string, string>;
  fields: TargetField[];
  available: boolean;
}

export interface PresetView {
  key: string;
  label: string;
  sheet: string;
  position: number;
  target_key: string | null;
  note: string;
  available: boolean;
}

export interface Batch {
  id: string;
  target_key: string;
  preset_key: string | null;
  sheet_name: string | null;
  filename: string;
  file_sha256: string;
  headers: string[];
  rows_total: number;
  mapping: Record<string, string> | null;
  defaults: Record<string, unknown>;
  status: BatchStatus;
  valid_rows: number;
  invalid_rows: number;
  created_rows: number;
  updated_rows: number;
  skipped_rows: number;
  failed_rows: number;
  applied_at: string | null;
  created_at: string;
}

export interface RowError {
  batch_id: string;
  row_number: number;
  status: string;
  field: string | null;
  column_name: string | null;
  message: string;
}

export interface PreviewRow {
  row: number;
  status: string;
  mapped: Record<string, unknown> | null;
  errors: Array<{ field?: string; column?: string; message: string }>;
  already_imported: boolean;
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "onboarding.create_batch": {
      args: {
        p_org: U; p_target_key: string; p_filename: string; p_file_sha256: string; p_headers: string[];
        p_rows: Array<{ row: number; values: Record<string, string | number | boolean | null> }>; p_sheet_name?: string | null; p_preset?: string | null;
      };
      returns: U;
    };
    "onboarding.suggest_mapping": { args: { p_batch: U }; returns: Record<string, string> };
    "onboarding.set_mapping": { args: { p_batch: U; p_mapping: Record<string, string>; p_defaults?: Record<string, unknown> }; returns: { batch_id: U; status: string } };
    "onboarding.validate_batch": { args: { p_batch: U }; returns: { batch_id: U; status: string; valid: number; invalid: number } };
    "onboarding.preview_batch": {
      args: { p_batch: U; p_limit?: number };
      returns: { batch_id: U; status: string; target: string; counts: { valid: number; invalid: number; new: number; already_imported: number }; rows: PreviewRow[] };
    };
    "onboarding.apply_batch": {
      args: { p_batch: U };
      returns: { batch_id: U; status: string; idempotent: boolean; created: number; updated: number; skipped: number; failed: number; invalid: number };
    };
    "onboarding.error_report": { args: { p_batch: U }; returns: string };
  }
}

export function useTargets() {
  return useQuery({
    queryKey: ["onboarding", "targets"],
    queryFn: () => unwrap<TargetView[]>(from("onboarding", "v_targets").select("*").order("label")),
    staleTime: 60_000,
  });
}

export function usePresets() {
  return useQuery({
    queryKey: ["onboarding", "presets"],
    queryFn: () => unwrap<PresetView[]>(from("onboarding", "v_presets").select("*").order("position")),
    staleTime: 60_000,
  });
}

export async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
